import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';
import Icon from '@welldev-group/rcl-icon';
import Input from '@welldev-group/rcl-input';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import { Text } from '@welldev-group/rcl-typography';
import { I18nText } from '@welldev-group/react-comp-lib';
import Modal from '@welldev-group/rcl-modal';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import selectBoxObjectMapper from '../../../shared/helper/selectBoxObjectMapper';

import PermissionsSelection from './PermissionsSelection';

const UserModal = ({
  title,
  linkText,
  showPasswordFields,
  showAffiliateSelection,
  permissionTemplateOptions,
  fetchUsersByAffiliate,
  permissionOptions,
  submitURL,
  userAffiliateId,
  linkClassName,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [affiliateId, setAffiliateId] = useState(userAffiliateId);
  const [teamIds, setTeamIds] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState({});

  const resetUserFormFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPermissions([]);
    setPassword('');
    setConfirmPassword('');
    setAffiliates([]);
    setTeams([]);
  };

  const createUser = async () => {
    const createUserParams = {
      first_name: firstName,
      last_name: lastName,
      email,
      permissions,
      password,
      affiliate_id: affiliateId,
      team_ids: teamIds,
    };

    const { data } = await httpClient.post(submitURL(), {
      user: createUserParams,
    });

    if (data.error === null) {
      resetUserFormFields();
      setModalOpen(false);
      fetchUsersByAffiliate();
    }
  };

  const onCreateUser = () =>
    createUser().catch(({ response }) => {
      if (response.status === 422) {
        setError(response.data.error);
      }
    });

  const disableSubmitButton = () =>
    !firstName ||
    !lastName ||
    !email ||
    permissions.length < 1 ||
    (showPasswordFields && !password) ||
    (showPasswordFields && !confirmPassword) ||
    (showAffiliateSelection && !affiliateId);

  const fetchAffiliates = debounce(500, false, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.affiliates.list({ q: inputValue }),
      );
      setAffiliates(
        data.affiliates.map(affiliate => ({
          value: affiliate.id,
          label: affiliate.clientName,
        })),
      );
    }
  });

  const fetchTeams = debounce(500, false, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.teams.list({ q: inputValue, affiliate_id: affiliateId }),
      );

      const teamOptions = data.teams.map(team => ({
        label: team.name,
        value: team.id,
      }));
      setTeams(teamOptions);
    }
  });

  const fetchAffiliate = async id => {
    const { data } = await httpClient.get(routes.affiliates.fetch({ id }));

    if(data){
      setAffiliates([{ value: id, label: data.affiliate.clientName }]);
    }
  };

  useEffect(() => {
    if (userAffiliateId && showAffiliateSelection) {
      fetchAffiliate(userAffiliateId);
    }
  }, [userAffiliateId, showAffiliateSelection]);

  const onAffiliateIdChange = selectedOption => {
    setAffiliateId(selectedOption && selectedOption.value);
    setTeamIds([]);
    setTeams([]);
  };
  const onTeamIdsChange = selectedOption => {
    setTeamIds(selectedOption && selectedOption.map(team => team.value));
  };

  const passwordNotMatching = password !== confirmPassword;

  return (
    <div className="user-modal__button">
      <Button
        className={linkClassName}
        version="v2"
        size="small"
        fullWidth={false}
        type="primary"
        label={linkText}
        onClick={() => {
          setModalOpen(true);
        }}
      />
      <Modal
        className="modal__wrapper"
        isVisible={modalOpen}
        onOutsideClick={() => setModalOpen(false)}
      >
        <>
          <div className="modal__header">
            <Modal.Title>{title}</Modal.Title>
            <div
              onClick={() => setModalOpen(false)}
              onKeyDown={() => setModalOpen(false)}
              role="presentation"
            >
              <Icon name="close" size="normal" />
            </div>
          </div>
          <Modal.Content>
            <div className="grid">
              <div className="col-12">
                <Text level="1" weight="bold" isInline={true}>
                  <I18nText id="user.form.general_information" />
                </Text>
              </div>
              <div className="col-lg-6 col-md-12">
                <Input
                  onChange={value => setFirstName(value)}
                  placeholder={I18n.t('components.user.form.first_name')}
                  value={firstName}
                  error={error.firstName}
                  touched={true}
                />
              </div>
              <div className="col-lg-6 col-md-12">
                <Input
                  onChange={value => setLastName(value)}
                  placeholder={I18n.t('components.user.form.last_name')}
                  value={lastName}
                  error={error.lastName}
                  touched={true}
                />
              </div>
              <div className="col-lg-6 col-md-12">
                <Input
                  onChange={value => setEmail(value)}
                  placeholder={I18n.t('components.user.form.email')}
                  value={email}
                  error={error.email}
                  touched={true}
                />
              </div>
              {showPasswordFields && (
                <div className="col-lg-6 col-md-12">
                  <Input
                    onChange={value => setPassword(value)}
                    placeholder={I18n.t('components.user.form.password')}
                    value={password}
                    error={error.password}
                    type="password"
                    touched={true}
                  />
                </div>
              )}
              {showPasswordFields && (
                <div className="col-lg-6 col-md-12">
                  <Input
                    onChange={value => setConfirmPassword(value)}
                    placeholder={I18n.t(
                      'components.user.form.confirm_password',
                    )}
                    value={confirmPassword}
                    error={
                      passwordNotMatching && (
                        <I18nText id="user.form.validations.password" />
                      )
                    }
                    type="password"
                    touched={true}
                  />
                </div>
              )}
              {showAffiliateSelection && (
                <div className="col-lg-6 col-md-12 user-modal__button-selectBox--notmulti">
                  <SelectBox
                    onChange={onAffiliateIdChange}
                    onInputChange={fetchAffiliates}
                    options={affiliates}
                    placeholderText={<I18nText id="user.form.affiliate" />}
                    required={true}
                    width="full"
                    value={selectBoxObjectMapper(affiliates, affiliateId)}
                  />
                </div>
              )}
              <div className="col-lg-6 col-md-12">
                <SelectBox
                  className="user-modal__team-select"
                  onChange={onTeamIdsChange}
                  onInputChange={fetchTeams}
                  options={teams}
                  placeholderText={<I18nText id="user.form.teams" />}
                  required={false}
                  isDisabled={!affiliateId}
                  isMulti={true}
                  width="full"
                  value={teams.map(
                    item => teamIds.includes(item.value) && item,
                  )}
                />
              </div>
              <PermissionsSelection
                permissionTemplateOptions={permissionTemplateOptions}
                permissionOptions={permissionOptions}
                onChange={setPermissions}
                selectedPermissions={permissions}
              />
            </div>
          </Modal.Content>
          <Modal.Footer>
            <div className="col-12">
              <div className="user-modal__save">
                <Button
                  version="v2"
                  size="tiny"
                  label={<I18nText id="user.actions.save" />}
                  type="primary"
                  onClick={onCreateUser}
                  disabled={disableSubmitButton()}
                />
              </div>
              <div className="user-modal__cancel">
                <Button
                  version="v2"
                  size="tiny"
                  label={<I18nText id="user.actions.cancel" />}
                  onClick={() => setModalOpen(false)}
                />
              </div>
            </div>
          </Modal.Footer>
        </>
      </Modal>
    </div>
  );
};

UserModal.defaultProps = {
  permissionTemplateOptions: [],
  showAffiliateSelection: false,
  showPasswordFields: true,
};

UserModal.propTypes = {
  title: PropTypes.node.isRequired,
  linkText: PropTypes.node.isRequired,
  showPasswordFields: PropTypes.bool,
  showAffiliateSelection: PropTypes.bool,
  fetchUsersByAffiliate: PropTypes.func.isRequired,
  submitURL: PropTypes.string.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  linkClassName: PropTypes.string.isRequired,
};

export default UserModal;
